import { DateTime } from 'luxon';

export const TIMEZONE_CODE_MAP: { [key: string]: string } = {
  'US/Central': 'America/Chicago',
  'US/Eastern': 'America/New_York',
  'US/Pacific': 'America/Los_Angeles',
};

export function mapTimezoneToIana(timezone?: string) {
  if (!timezone) return undefined;
  const trimmed = timezone.trim();
  return TIMEZONE_CODE_MAP[trimmed] ?? trimmed;
}

export function getTimezoneAbbreviation(timezone?: string | null, date: Date = new Date()): string | undefined {
  const mappedTimezone = mapTimezoneToIana(timezone ?? undefined);
  if (!mappedTimezone) return undefined;

  const dateTime = DateTime.fromJSDate(date).setLocale('en-US').setZone(mappedTimezone);
  return dateTime.isValid ? (dateTime.offsetNameShort ?? undefined) : undefined;
}

/**
 * This function returns a new string that will be treated as UTC regardless of the server/computer's timezone.
 * Example:
 *  - `2024-10-08T14:10:45.266964` - if you make this a Date object, in the Philippines it will be:
 *      `Tue Oct 08 2024 14:10:45 GMT+0800 (Philippine Standard Time)`
 *     - but if treated as UTC, it should be
 *      `Tue Oct 08 2024 22:10:45 GMT+0800 (Philippine Standard Time)`.
 *     - This function would return `2024-10-08T14:10:45.000Z`
 * @param dateString
 * @returns ISOString indicating UTC timezone
 */
export function getCompleteUtcDateString(dateString: string) {
  const parsedDate = new Date(dateString);
  if (isNaN(parsedDate.getTime())) {
    throw 'Invalid Date';
  }

  const utcEpoch = Date.UTC(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
    parsedDate.getHours(),
    parsedDate.getMinutes(),
    parsedDate.getSeconds(),
  );

  return new Date(utcEpoch).toISOString();
}

export function convertDateToTimezone(date: string | Date, timezone?: string | null): Date {
  if (!timezone) {
    return date instanceof Date ? date : new Date(date);
  }
  const dateTime =
    typeof date === 'string'
      ? DateTime.fromISO(date, { zone: 'utc' }).setZone(timezone)
      : DateTime.fromJSDate(date, { zone: 'utc' }).setZone(timezone);

  if (!dateTime.isValid) {
    return date instanceof Date ? date : new Date(date);
  }

  return new Date(dateTime.toISO({ includeOffset: false }));
}
